import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  GraduationCap,
  Zap,
  Bell,
  Trophy,
  BarChart3,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const features = [
    {
      icon: BookOpen,
      title: 'Video Lectures',
      description: 'Learn from comprehensive online lectures for all subjects',
    },
    {
      icon: GraduationCap,
      title: 'Structured Courses',
      description: 'Organized by class level from JSS1 to SS3',
    },
    {
      icon: Zap,
      title: 'Monthly Exams',
      description: 'Test your knowledge on the 10th day of each month',
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Never miss an exam or important announcement',
    },
    {
      icon: Trophy,
      title: 'Rankings & Results',
      description: 'Track your performance and class rankings',
    },
    {
      icon: BarChart3,
      title: 'Digital Library',
      description: 'Access organized textbooks and study resources',
    },
  ];

  const classes = [
    { name: 'JSS1', students: 120, courses: 15 },
    { name: 'JSS2', students: 115, courses: 15 },
    { name: 'JSS3', students: 108, courses: 15 },
    { name: 'SS1', students: 95, courses: 18 },
    { name: 'SS2', students: 88, courses: 18 },
    { name: 'SS3', students: 92, courses: 20 },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-lg text-primary hidden sm:inline">
                Hossanah Baptist
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium hover:text-primary transition">
                Features
              </a>
              <a href="#classes" className="text-sm font-medium hover:text-primary transition">
                Classes
              </a>
              <a href="#contact" className="text-sm font-medium hover:text-primary transition">
                Contact
              </a>
            </div>

            <div className="hidden md:flex gap-3">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5">
                Sign In
              </Button>
              <Button className="bg-primary hover:bg-primary/90">Get Started</Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-muted rounded-lg transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border space-y-3">
              <a href="#features" className="block text-sm font-medium hover:text-primary py-2">
                Features
              </a>
              <a href="#classes" className="block text-sm font-medium hover:text-primary py-2">
                Classes
              </a>
              <a href="#contact" className="block text-sm font-medium hover:text-primary py-2">
                Contact
              </a>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1">
                  Sign In
                </Button>
                <Button size="sm" className="flex-1 bg-primary">
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center space-y-8"
          >
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-tight"
            >
              Welcome to Hossanah Baptist
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                Model School
              </span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
            >
              Unlock your potential through world-class online learning, comprehensive exams, and personalized study guidance. Master every subject with our innovative education platform.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
            >
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 h-12 px-8 text-base gap-2"
              >
                Start Learning Today <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 h-12 px-8 text-base"
              >
                Learn More
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-4 pt-8 max-w-md mx-auto"
            >
              <div className="bg-white rounded-lg p-4 border border-border">
                <div className="text-2xl font-bold text-primary">6</div>
                <div className="text-sm text-muted-foreground">Classes</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-border">
                <div className="text-2xl font-bold text-secondary">100+</div>
                <div className="text-sm text-muted-foreground">Courses</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-border">
                <div className="text-2xl font-bold text-accent">600+</div>
                <div className="text-sm text-muted-foreground">Students</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-12"
          >
            <div className="text-center space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary">
                Everything You Need to Succeed
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A comprehensive platform designed to support your academic journey
              </p>
            </div>

            <motion.div
              variants={containerVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-gradient-to-br from-primary/5 to-transparent rounded-xl p-6 border border-border hover:border-secondary hover:shadow-lg transition-all duration-300"
                  >
                    <Icon className="w-10 h-10 text-secondary mb-4" />
                    <h3 className="text-xl font-semibold text-primary mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Classes Section */}
      <section
        id="classes"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-primary/5"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-12"
          >
            <div className="text-center space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary">
                Our Classes
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From JSS1 to SS3, we provide education excellence across all levels
              </p>
            </div>

            <motion.div
              variants={containerVariants}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {classes.map((cls, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 border border-border hover:border-primary hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-primary">{cls.name}</h3>
                    <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-secondary" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Students</span>
                      <span className="font-semibold text-primary">{cls.students}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Courses</span>
                      <span className="font-semibold text-secondary">{cls.courses}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-secondary">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl sm:text-5xl font-bold text-white"
            >
              Ready to Achieve Excellence?
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-lg text-white/90 mt-4 max-w-2xl mx-auto"
            >
              Join thousands of students who are already transforming their academic performance. Start your journey with Hossanah Baptist Model School today.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-8"
            >
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/90 h-12 px-8 text-base font-semibold gap-2"
              >
                Create Account <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 h-12 px-8 text-base"
              >
                Schedule Demo
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        id="contact"
        className="bg-primary text-white py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <span className="font-bold">Hossanah Baptist</span>
              </div>
              <p className="text-white/70 text-sm">
                Excellence in Education, Growth in Faith
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#classes" className="hover:text-white transition">Classes</a></li>
                <li><a href="#" className="hover:text-white transition">Support</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition">Documentation</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-sm text-white/70">
                <div>Email: info@hossanah.edu</div>
                <div>Phone: +234 XXX XXXX</div>
              </p>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-center text-sm text-white/70">
              © 2025 Hossanah Baptist Model School. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
